import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, LoginCredentials, RegisterData } from '../types';
import { authService } from '../services/auth';
import { userService } from '../services/user';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🔄 AuthContext: Initializing auth...');
      try {
        if (authService.isAuthenticated()) {
          console.log('🔑 AuthContext: Token found in localStorage');
          const currentUser = authService.getCurrentUser();
          if (currentUser) {
            console.log('👤 AuthContext: User found in localStorage:', currentUser);
            setUser(currentUser);
            // Optionally refresh user data from server, but don't fail if it doesn't work
            try {
              console.log('🔄 AuthContext: Refreshing user profile from server...');
              const freshUser = await userService.getMe();
              console.log('✅ AuthContext: Fresh user data received:', freshUser);
              setUser(freshUser);
              localStorage.setItem('user', JSON.stringify(freshUser));
            } catch (error) {
              console.warn('⚠️ AuthContext: Failed to refresh user profile, using cached data:', error);
              // Keep the cached user data, don't logout
            }
          } else {
            console.log('❌ AuthContext: No user data found in localStorage');
          }
        } else {
          console.log('❌ AuthContext: No token found - user not authenticated');
        }
      } catch (error) {
        console.error('❌ AuthContext: Error initializing auth:', error);
        // Only clear auth data if there's a real authentication issue
        const errorMessage = (error as any)?.message || '';
        if (errorMessage.toLowerCase().includes('unauthenticated') || 
            errorMessage.toLowerCase().includes('token')) {
          console.log('🚨 AuthContext: Authentication error detected - logging out');
          authService.logout();
        }
      } finally {
        console.log('✅ AuthContext: Auth initialization complete');
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      setIsLoading(true);
      console.log('🔄 AuthContext: Starting login process...');
      const authData = await authService.login(credentials);
      
      // Buscar dados completos do usuário após login
      try {
        console.log('🔄 AuthContext: Fetching complete user profile...');
        const completeUser = await userService.getMe();
        console.log('✅ AuthContext: Complete user profile received:', completeUser);
        setUser(completeUser);
        localStorage.setItem('user', JSON.stringify(completeUser));
      } catch (error) {
        console.warn('⚠️ AuthContext: Failed to fetch complete profile, using login data:', error);
        setUser(authData.user);
      }
    } catch (error) {
      console.error('❌ AuthContext: Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      setIsLoading(true);
      console.log('🔄 AuthContext: Starting registration process...');
      
      // Register the user (this will create the account and return user data)
      const authData = await authService.register(userData);
      console.log('✅ AuthContext: Registration successful, but not logging user in automatically');
      
      // Clear any authentication data to ensure user goes to login
      authService.clearAuth();
      setUser(null);
      
      console.log('✅ AuthContext: Registration complete, user must login manually');
    } catch (error) {
      console.error('❌ AuthContext: Registration failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    // Limpar imediatamente os dados locais para logout rápido
    setUser(null);
    authService.clearAuth();
    
    // Tentar chamar a API em background, mas não bloquear a interface
    try {
      await authService.logout();
    } catch (error) {
      console.warn('Failed to logout on server, but local session cleared:', error);
    }
  };

  const refreshUser = async () => {
    try {
      if (!authService.isAuthenticated()) {
        throw new Error('User not authenticated');
      }
      
      console.log('🔄 AuthContext: Refreshing user profile...');
      const freshUser = await userService.getMe();
      console.log('✅ AuthContext: User profile refreshed:', freshUser);
      setUser(freshUser);
      localStorage.setItem('user', JSON.stringify(freshUser));
    } catch (error) {
      console.error('❌ AuthContext: Error refreshing user:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
